import { focusRingStyle, uiElementPaddingStyle } from "./styles.mjs";
import { Text } from "./Text.mjs";
import { useTheme } from "./ThemeProvider.mjs";
import { Flex } from "./Flex.mjs";
import { Icon } from "./Icon.mjs";
import { useStyles } from "purse-styles";
import React from "react";
import { match } from "@repo/utils/utils";
import { Button, Label, ListBox, ListBoxItem, Popover, Select as Select$1, SelectValue } from "react-aria-components";
import { jsx, jsxs } from "react/jsx-runtime";

//#region src/Select.tsx
const Select = ({ options, value, onSelectionChange, placeholder = "Select an option", disabled = false, label, "aria-label": ariaLabel, size = "md", variant = "default", minWidth = 100, style: style$1, error }) => {
	const theme = useTheme();
	const borderRadius = theme.borderRadiuses.md;
	const getVariantStyles = () => {
		if (variant === "ghost") return {
			background: "transparent",
			border: "none",
			"&:hover": disabled ? void 0 : { background: theme.colors.sandA3 },
			"&[data-pressed]": disabled ? void 0 : { background: theme.colors.sandA4 }
		};
		return {
			background: theme.colors.sand1,
			border: "none",
			boxShadow: theme.shadows.subtle,
			"&:hover": disabled ? void 0 : { background: theme.colors.sand2 },
			"&[data-pressed]": disabled ? void 0 : { background: theme.colors.sand2 }
		};
	};
	const iconClassName = useStyles({
		display: "flex",
		alignItems: "center",
		opacity: variant === "ghost" ? 0 : 1
	});
	const triggerClassName = useStyles(uiElementPaddingStyle({ size }), focusRingStyle({ theme }), {
		position: "relative",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		width: "100%",
		minWidth,
		borderRadius,
		color: theme.colors.sand12,
		gap: theme.spacing[match(size, {
			xs: "xxs",
			sm: "xxs",
			md: "sm"
		})],
		cursor: disabled ? "not-allowed" : void 0,
		opacity: disabled ? .6 : 1,
		...getVariantStyles(),
		[`&:hover .${iconClassName}`]: variant === "ghost" ? { opacity: 1 } : void 0,
		[`&[data-focus-visible] .${iconClassName}`]: variant === "ghost" ? { opacity: 1 } : void 0,
		[`&[data-pressed] .${iconClassName}`]: variant === "ghost" ? { opacity: 1 } : void 0
	});
	const popoverClassName = useStyles({
		background: theme.colors.sand1,
		borderRadius: theme.borderRadiuses.lg,
		boxShadow: theme.shadows.subtle,
		minWidth: "var(--trigger-width)",
		maxHeight: "200px",
		overflow: "auto",
		padding: "4px",
		transition: `opacity ${theme.transitions.fast.duration}ms ${theme.transitions.fast.easing}, transform ${theme.transitions.fast.duration}ms ${theme.transitions.fast.easing}`,
		"&[data-entering]": {
			opacity: 0,
			transform: "translateY(-4px)"
		},
		"&[data-exiting]": {
			opacity: 0,
			transform: "translateY(-4px)"
		},
		"@media (prefers-reduced-motion: reduce)": { transition: "none" }
	});
	const itemClassName = useStyles(uiElementPaddingStyle({ size }), focusRingStyle({ theme }), {
		display: "flex",
		alignItems: "center",
		borderRadius: theme.borderRadiuses.sm,
		color: theme.colors.sand12,
		userSelect: "none",
		"&[data-hovered]": { background: theme.colors.sand3 },
		"&[data-focused]": { background: theme.colors.sand3 }
	});
	return /* @__PURE__ */ jsxs(Select$1, {
		selectedKey: value,
		onSelectionChange: (key) => {
			onSelectionChange?.(key ?? void 0);
		},
		isDisabled: disabled,
		"aria-label": ariaLabel,
		style: {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing.xs,
			...style$1
		},
		children: [
			label && /* @__PURE__ */ jsx(Label, { children: /* @__PURE__ */ jsx(Text, {
				size: "sm",
				light: true,
				weight: "medium",
				children: label
			}) }),
			/* @__PURE__ */ jsxs(Button, {
				className: triggerClassName,
				children: [/* @__PURE__ */ jsx(SelectValue, {
					style: {
						flex: 1,
						textAlign: "left"
					},
					children: ({ selectedText }) => /* @__PURE__ */ jsx(Text, {
						size: "sm",
						select: false,
						style: { color: selectedText ? theme.colors.sand12 : theme.colors.sand11 },
						children: selectedText || placeholder
					})
				}), /* @__PURE__ */ jsx("span", {
					className: iconClassName,
					children: /* @__PURE__ */ jsx(Icon, {
						name: "ChevronDown",
						size: "sm",
						color: "sand11"
					})
				})]
			}),
			/* @__PURE__ */ jsx(Popover, {
				className: popoverClassName,
				children: /* @__PURE__ */ jsx(ListBox, { children: options.map((option, index) => /* @__PURE__ */ jsx(ListBoxItem, {
					id: option.value,
					className: itemClassName,
					textValue: option.label,
					children: ({ isSelected }) => /* @__PURE__ */ jsxs(Flex, {
						inline: true,
						flex: true,
						align: "center",
						gap: "sm",
						justify: "space-between",
						children: [/* @__PURE__ */ jsxs(Flex, {
							align: "center",
							gap: "xs",
							style: option.indent ? { paddingLeft: theme.spacing.lg } : void 0,
							children: [option.icon && /* @__PURE__ */ jsx(Icon, {
								name: option.icon,
								size: "sm",
								color: option.iconColor ?? "sand11",
								shrink: 0
							}), /* @__PURE__ */ jsx(Text, {
								size: "sm",
								select: false,
								light: !option.value,
								weight: isSelected ? "semibold" : void 0,
								children: option.label
							})]
						}), isSelected && /* @__PURE__ */ jsx(Icon, {
							name: "Check",
							size: "sm",
							color: "accent11"
						})]
					})
				}, option.value ?? `placeholder-${index}`)) })
			}),
			error && /* @__PURE__ */ jsx(Text, {
				size: "sm",
				color: "red11",
				children: error
			})
		]
	});
};

//#endregion
export { Select };